import { useEffect } from 'react';
import styled from 'styled-components';
import { Form, Input, Button, Modal, Pagination } from 'antd';
import { IdcardOutlined, UserOutlined, LockOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { MainColor } from '../../style/styleValue';
import { ActivityItem } from './types';
import { useMessages } from '../../i18n';

const ko = {
  submit: '변경하기',
  nameTitle: '이름 변경',
  nameRequired: '변경할 이름을 입력해주세요.',
  namePh: '새로운 이름',
  idTitle: '아이디 변경',
  idWarning: '아이디 변경 완료 시, 자동으로 로그아웃되며 다시 로그인해야 합니다.',
  idRequired: '변경할 아이디를 입력해주세요.',
  idPh: '새로운 아이디',
  verifyPasswordRequired: '본인 확인을 위해 현재 비밀번호를 입력해주세요.',
  currentPasswordPh: '현재 비밀번호',
  passwordTitle: '비밀번호 변경',
  currentPasswordRequired: '현재 비밀번호를 입력해주세요.',
  newPasswordRequired: '새로운 비밀번호를 입력해주세요.',
  newPasswordPh: '새 비밀번호',
  confirmRequired: '새 비밀번호를 다시 입력해주세요.',
  confirmMismatch: '새 비밀번호가 일치하지 않습니다.',
  confirmPh: '새 비밀번호 확인',
  loading: '목록을 불러오는 중입니다...',
  noContent: '내용 없음',
  empty: '항목이 없습니다.',
};

const en: typeof ko = {
  submit: 'Save',
  nameTitle: 'Change Name',
  nameRequired: 'Please enter a new name.',
  namePh: 'New name',
  idTitle: 'Change Username',
  idWarning: 'Once your username is changed, you will be logged out and need to log in again.',
  idRequired: 'Please enter a new username.',
  idPh: 'New username',
  verifyPasswordRequired: 'Please enter your current password to verify your identity.',
  currentPasswordPh: 'Current password',
  passwordTitle: 'Change Password',
  currentPasswordRequired: 'Please enter your current password.',
  newPasswordRequired: 'Please enter a new password.',
  newPasswordPh: 'New password',
  confirmRequired: 'Please enter the new password again.',
  confirmMismatch: 'The new passwords do not match.',
  confirmPh: 'Confirm new password',
  loading: 'Loading...',
  noContent: 'No content',
  empty: 'No items.',
};

export const NameChangeModal = ({ isOpen, onClose, onSubmit, isLoading, currentName }: any) => {
  const [form] = Form.useForm();
  const t = useMessages({ ko, en });
  
  useEffect(() => {
    if (isOpen) form.setFieldsValue({ name: currentName });
  }, [isOpen, currentName, form]);

  return (
    <StyledModal title={t.nameTitle} open={isOpen} onCancel={onClose} footer={null} centered>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item name="name" rules={[{ required: true, message: t.nameRequired }]}>
          <Input prefix={<IdcardOutlined className="icon" />} placeholder={t.namePh} />
        </Form.Item>
        <SubmitButton type="primary" htmlType="submit" loading={isLoading}>{t.submit}</SubmitButton>
      </Form>
    </StyledModal>
  );
};

export const IdChangeModal = ({ isOpen, onClose, onSubmit, isLoading, currentUsername }: any) => {
  const [form] = Form.useForm();
  const t = useMessages({ ko, en });

  useEffect(() => {
    if (isOpen) form.setFieldsValue({ newUsername: currentUsername, currentPassword: '' }); 
  }, [isOpen, currentUsername, form]);

  return (
    <StyledModal title={t.idTitle} open={isOpen} onCancel={onClose} footer={null} centered>
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <WarningAlert>
          <ExclamationCircleOutlined style={{ marginRight: '6px' }} />
          {t.idWarning}
        </WarningAlert>
        <Form.Item name="newUsername" normalize={(v) => (v || '').replace(/\s/g, '')} rules={[{ required: true, message: t.idRequired }]}>
          <Input prefix={<UserOutlined className="icon" />} placeholder={t.idPh} />
        </Form.Item>
        <Form.Item name="currentPassword" rules={[{ required: true, message: t.verifyPasswordRequired }]}>
          <Input.Password prefix={<LockOutlined className="icon" />} placeholder={t.currentPasswordPh} />
        </Form.Item>
        <SubmitButton type="primary" htmlType="submit" loading={isLoading}>{t.submit}</SubmitButton>
      </Form>
    </StyledModal>
  );
};

export const PasswordChangeModal = ({ isOpen, onClose, onSubmit, isLoading }: any) => {
  const t = useMessages({ ko, en });
  return (
    <StyledModal title={t.passwordTitle} open={isOpen} onCancel={onClose} footer={null} centered>
      <Form layout="vertical" onFinish={onSubmit}>
        <Form.Item name="currentPassword" rules={[{ required: true, message: t.currentPasswordRequired }]}>
          <Input.Password prefix={<LockOutlined className="icon" />} placeholder={t.currentPasswordPh} />
        </Form.Item>
        <Form.Item name="newPassword" normalize={(v) => (v || '').replace(/\s/g, '')} rules={[{ required: true, message: t.newPasswordRequired }]}>
          <Input.Password prefix={<LockOutlined className="icon" />} placeholder={t.newPasswordPh} />
        </Form.Item>
        <Form.Item name="confirmPassword" dependencies={['newPassword']} rules={[
          { required: true, message: t.confirmRequired },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
              return Promise.reject(new Error(t.confirmMismatch));
            },
          }),
        ]}>
          <Input.Password prefix={<LockOutlined className="icon" />} placeholder={t.confirmPh} />
        </Form.Item>
        <SubmitButton type="primary" htmlType="submit" loading={isLoading}>{t.submit}</SubmitButton>
      </Form>
    </StyledModal>
  );
};

export const ViewAllModal = ({ 
  isOpen, onClose, title, items, isLoading, 
  currentPage, totalItems, pageSize, onPageChange, onItemClick 
}: any) => {
  const t = useMessages({ ko, en });
  return (
    <StyledModal title={title} open={isOpen} onCancel={onClose} footer={null} centered destroyOnClose>
      <ModalListContainer>
        {isLoading ? (
          <LoadingWrapper>{t.loading}</LoadingWrapper>
        ) : items.length > 0 ? (
          items.map((item: ActivityItem, idx: number) => (
            <ModalListItem key={item.id || idx}>
              <div 
                className="item-title" 
                onClick={() => onItemClick && onItemClick(item)}
              >
                {item.title || t.noContent}
              </div>
              <div className="item-date">{item.date}</div>
            </ModalListItem>
          ))
        ) : (
          <LoadingWrapper>{t.empty}</LoadingWrapper>
        )}
      </ModalListContainer>
      
      {items.length > 0 && !isLoading && (
        <PaginationWrapper>
          <Pagination 
            current={currentPage} 
            total={totalItems} 
            pageSize={pageSize} 
            onChange={onPageChange} 
            size="small" 
            showSizeChanger={false}
          />
        </PaginationWrapper>
      )}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-content {
    padding: 30px;
    border-radius: 12px;
  }
  
  .ant-modal-title {
    font-size: 18px;
    font-weight: 700;
    color: #17375f;
    margin-bottom: 20px;
  }
  
  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-input-affix-wrapper {
    padding: 12px 14px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #fafafa;
    transition: all 0.2s ease;

    input {
      background-color: transparent;
      font-size: 15px;
      color: #333;
      
      &::placeholder {
        color: #bbb;
      }
    }

    .icon {
      color: #a0a0a0;
      font-size: 18px;
      margin-right: 8px;
      transition: color 0.3s;
    }

    &:focus,
    &-focused {
      background-color: #fff;
      border-color: #17375f;
      box-shadow: 0 0 0 3px rgba(23, 55, 95, 0.1);
      
      .icon {
        color: #17375f;
      }
    }
  }
`;

const WarningAlert = styled.div`
  display: flex;
  align-items: center;
  background-color: #fff1f0;
  border: 1px solid #ffccc7;
  color: #cf1322;
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 13px;
  line-height: 1.4;
`;

const SubmitButton = styled(Button)`
  width: 100%;
  height: 48px;
  margin-top: 10px;
  background-color: ${MainColor || '#17375f'};
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  box-shadow: none;

  &:hover, &:focus {
    background-color: #102a4a !important;
    color: white !important;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalListContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  padding-right: 5px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }
`;

const ModalListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  .item-title {
    font-size: 14px;
    color: #333;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 75%;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: #17375f;
      text-decoration: underline;
    }
  }

  .item-date {
    font-size: 12px;
    color: #888;
  }
`;

const LoadingWrapper = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #888;
  font-size: 14px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`;